import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { http, API } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "darkex_jwt";

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Axios interceptor: attach token, handle 401 globally
  useEffect(() => {
    const reqId = http.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    const resId = http.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
          if (!window.location.pathname.startsWith("/login")
              && !window.location.pathname.startsWith("/signup")) {
            window.location.href = "/login";
          }
        }
        return Promise.reject(err);
      }
    );
    return () => {
      http.interceptors.request.eject(reqId);
      http.interceptors.response.eject(resId);
    };
  }, []);

  // Validate token on mount
  useEffect(() => {
    let cancel = false;
    const init = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancel) setUser(data);
      } catch {
        if (!cancel) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    init();
    return () => { cancel = true; };
  }, [token]);

  const login = useCallback((newToken, newUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
